import React from 'react';

import {
	post,
} from '~/model/api/API';



type MeltingspotTokenResponse = {
	token: string,
};

const TOKEN_CACHE_DURATION_MS = 10 * 60 * 1000; // 10 minutes - sessionStorage expiry
const TOKEN_AUTO_REFRESH_INTERVAL_MS = 23 * 60 * 60 * 1000; // 23 hours - auto-refresh for long-lived sessions
const SESSION_STORAGE_KEY_TOKEN = 'meltingspot_token';
const SESSION_STORAGE_KEY_EXPIRY = 'meltingspot_token_expiry';

let fetchPromise: Promise<string> | null = null;
let cacheClearedAt: number | null = null;

function getCachedToken(): string | null {
	if (!('sessionStorage' in window)) {
		return null;
	}

	let token: string | null;
	let expiry: string | null;

	try {
		token = sessionStorage.getItem(SESSION_STORAGE_KEY_TOKEN);
		expiry = sessionStorage.getItem(SESSION_STORAGE_KEY_EXPIRY);
	} catch {
		return null;
	}

	if (token === null || expiry === null) {
		return null;
	}

	const expiryTime = parseInt(expiry, 10);

	if (isNaN(expiryTime) || Date.now() >= expiryTime) {
		sessionStorage.removeItem(SESSION_STORAGE_KEY_TOKEN);
		sessionStorage.removeItem(SESSION_STORAGE_KEY_EXPIRY);
		return null;
	}

	return token;
}

function setCachedToken(token: string): void {
	if (!('sessionStorage' in window)) {
		return;
	}

	// Don't cache if cache was cleared after we started fetching
	if (cacheClearedAt !== null) {
		return;
	}

	const expiryTime = Date.now() + TOKEN_CACHE_DURATION_MS;

	try {
		sessionStorage.setItem(SESSION_STORAGE_KEY_TOKEN, token);
		sessionStorage.setItem(SESSION_STORAGE_KEY_EXPIRY, expiryTime.toString());
	} catch {
		// Silently fail - storage unavailable, token will be refetched
	}
}

function clearCachedToken(): void {
	if (!('sessionStorage' in window)) {
		return;
	}

	try {
		sessionStorage.removeItem(SESSION_STORAGE_KEY_TOKEN);
		sessionStorage.removeItem(SESSION_STORAGE_KEY_EXPIRY);
		fetchPromise = null; // Clear any pending fetch promise
		cacheClearedAt = Date.now();
	} catch {
		// Silently fail - storage unavailable
	}
}

async function fetchMeltingspotToken(): Promise<string> {
	const cachedToken = getCachedToken();

	if (cachedToken !== null) {
		return cachedToken;
	}

	if (fetchPromise !== null) {
		return fetchPromise;
	}

	cacheClearedAt = null;

	fetchPromise = post('/community/token', {})
		.then((response: MeltingspotTokenResponse) => {
			setCachedToken(response.token);
			return response.token;
		})
		.finally(() => {
			fetchPromise = null;
		});

	return fetchPromise;
}

function useMeltingspotToken() {
	const cachedTokenValue = getCachedToken();
	const [token, setToken] = React.useState<string | null>(cachedTokenValue);
	const [loading, setLoading] = React.useState(cachedTokenValue === null);
	const [error, setError] = React.useState<string | null>(null);

	React.useEffect(() => {
		const cachedTokenFromStorage = getCachedToken();

		if (cachedTokenFromStorage !== null) {
			setToken(cachedTokenFromStorage);
			setLoading(false);
		} else {
			setLoading(true);
			setError(null);

			fetchMeltingspotToken()
				.then((fetchedToken) => {
					setToken(fetchedToken);
					setLoading(false);
				})
				.catch((err: any) => {
					setError(err?.json?.message || 'Failed to connect to community service');
					setLoading(false);
				});
		}

		const checkInterval = setInterval(() => {
			setLoading(true);
			setError(null);

			fetchMeltingspotToken()
				.then((fetchedToken) => {
					setToken(fetchedToken);
					setLoading(false);
				})
				.catch((err: any) => {
					setError(err?.json?.message || 'Failed to connect to community service');
					setLoading(false);
				});
		}, TOKEN_AUTO_REFRESH_INTERVAL_MS);

		return () => {
			clearInterval(checkInterval);
		};
	}, []);

	return {
		token,
		loading,
		error,
	};
}


export default useMeltingspotToken;
export {
	clearCachedToken,
};
